import { useMemo, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { profileLabel } from "@/config/uiConfig";

/**
 * An administrator previewing the application as someone else.
 *
 * Two ways: one existing account, or a role (a profile and, for a client profile,
 * a tenant: a typical account of it, which exists only for each request). The
 * target is kept for the browser tab (sessionStorage): a reload keeps the preview,
 * a new tab does not. Every API request then carries it (see `withPreview`), and
 * the API answers as that identity, read-only (backend `api/preview.py`).
 * Navigation, sections and data all follow, because they all come from the API.
 */
export const PREVIEW_PARAM = "preview_as";
export const PREVIEW_PROFILE_PARAM = "preview_profile";
export const PREVIEW_TENANT_PARAM = "preview_tenant";
const STORAGE_KEY = "nexus.preview";

export type PreviewTarget =
  | {
      kind: "account";
      accountId: string;
      email: string;
      profile: string;
      /** When it started (ms): only answers received after it can confirm or refute it. */
      startedAt: number;
    }
  | {
      kind: "role";
      profile: string;
      /** Set for a client profile: the tenant whose clients are previewed. */
      tenantId?: string;
      tenantLabel?: string;
      startedAt: number;
    };

/** A preview to start: the target without its start time. */
export type NewPreview =
  | Omit<Extract<PreviewTarget, { kind: "account" }>, "startedAt">
  | Omit<Extract<PreviewTarget, { kind: "role" }>, "startedAt">;

/** How the previewed identity reads: an account's email, or "Client viewer · Northstar Sports". */
export function previewLabel(target: PreviewTarget | NewPreview): string {
  if (target.kind === "account") return target.email;
  return [profileLabel(target.profile), target.tenantLabel].filter(Boolean).join(" · ");
}

const listeners = new Set<() => void>();
let cachedRaw: string | null = null;
let cachedTarget: PreviewTarget | null = null;

function readRaw(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function parse(value: unknown): PreviewTarget | null {
  if (!value || typeof value !== "object") return null;
  const entry = value as Record<string, unknown>;
  const startedAt = typeof entry.startedAt === "number" ? entry.startedAt : 0;
  if (entry.kind === "role" && typeof entry.profile === "string" && entry.profile) {
    return {
      kind: "role",
      profile: entry.profile,
      tenantId: typeof entry.tenantId === "string" ? entry.tenantId : undefined,
      tenantLabel: typeof entry.tenantLabel === "string" ? entry.tenantLabel : undefined,
      startedAt,
    };
  }
  // An account preview, including one saved before there were two kinds.
  if (
    (entry.kind === "account" || entry.kind === undefined) &&
    typeof entry.accountId === "string" &&
    entry.accountId
  ) {
    return {
      kind: "account",
      accountId: entry.accountId,
      email: typeof entry.email === "string" ? entry.email : "",
      profile: typeof entry.profile === "string" ? entry.profile : "",
      startedAt,
    };
  }
  return null;
}

/** The current preview, or `null`. The same object until it changes. */
export function readPreview(): PreviewTarget | null {
  const raw = readRaw();
  if (raw === cachedRaw) return cachedTarget;
  cachedRaw = raw;
  cachedTarget = null;
  if (raw) {
    try {
      cachedTarget = parse(JSON.parse(raw));
    } catch {
      // A damaged value is no preview.
    }
  }
  return cachedTarget;
}

export function writePreview(target: PreviewTarget | null) {
  try {
    if (target) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(target));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: the preview cannot be kept.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePreview(): PreviewTarget | null {
  return useSyncExternalStore(subscribe, readPreview, () => null);
}

/** `url` with the preview parameters when previewing. */
export function withPreview(url: string, target: PreviewTarget | null = readPreview()): string {
  if (!target) return url;
  const parameters: [string, string][] =
    target.kind === "account"
      ? [[PREVIEW_PARAM, target.accountId]]
      : [
          [PREVIEW_PROFILE_PARAM, target.profile],
          ...(target.tenantId
            ? ([[PREVIEW_TENANT_PARAM, target.tenantId]] as [string, string][])
            : []),
        ];
  const query = parameters.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join("&");
  return `${url}${url.includes("?") ? "&" : "?"}${query}`;
}

/**
 * Start or stop a preview. Everything cached was read as someone else, so the
 * cache is emptied and the application starts again from its landing page.
 */
export function usePreviewControls() {
  const client = useQueryClient();
  const navigate = useNavigate();
  return useMemo(
    () => ({
      start(target: NewPreview) {
        writePreview({ ...target, startedAt: Date.now() } as PreviewTarget);
        client.clear();
        navigate("/");
      },
      stop() {
        writePreview(null);
        client.clear();
        navigate("/");
      },
    }),
    [client, navigate],
  );
}
